import type { ColumnDef, Status } from "../types";
import { STATUS_STYLE } from "../constants";
import { BadgeSelect } from "./BadgeSelect";
import { EditableField } from "./EditableField";
import { StatusBadge } from "./StatusBadge";

interface Props {
  column: ColumnDef;
  value: string;
  onChange: (next: string) => void;
}

const STANDARD_STATUSES: Status[] = [
  "In Progress",
  "Waiting",
  "Pending",
  "Ongoing",
  "Not Started",
  "Complete",
];

function isStandardStatus(v: string): v is Status {
  return (STANDARD_STATUSES as string[]).includes(v);
}

export function EditableCell({ column, value, onChange }: Props) {
  switch (column.type) {
    case "status": {
      // Default to "Not Started" so the pill always renders something meaningful.
      const s: Status = isStandardStatus(value) ? value : "Not Started";
      const styled = STATUS_STYLE[s];
      // Render only when we have a valid style mapping (defensive against bad data).
      void styled;
      return <StatusBadge status={s} onChange={(v) => onChange(v)} />;
    }
    case "badge": {
      return (
        <BadgeSelect
          value={value}
          options={column.options ?? []}
          optionColors={column.optionColors}
          onChange={onChange}
          placeholder={`${column.label}...`}
        />
      );
    }
    case "longText": {
      return (
        <EditableField
          value={value}
          onChange={onChange}
          placeholder={`${column.label}...`}
          multi
          className="text-xs text-slate-600"
        />
      );
    }
    case "number": {
      return (
        <EditableField
          value={value}
          onChange={onChange}
          placeholder="0"
          className="text-xs text-slate-500 tabular-nums"
        />
      );
    }
    case "date": {
      return (
        <EditableField
          value={value}
          onChange={onChange}
          placeholder="Date..."
          className="text-xs text-slate-500"
        />
      );
    }
    case "text":
    default: {
      return (
        <EditableField
          value={value}
          onChange={onChange}
          placeholder={`${column.label}...`}
          className="text-xs text-slate-600"
        />
      );
    }
  }
}
